// «Invitar» in a call — the two post markers of shared/API.md §30.3 and the pure rules for
// RECEIVING them (twins: Swift / Kotlin / C# CallInviteSignal, same case names in the tests).
//
// * The INVITE is a DM from the inviter to each invitee: a Spanish sentence any client can read
//   (Mattermost's own webapp included) plus `props.satori_call_invite = {channel_id, call_id,
//   post_id}`. Our clients RING for it — the existing incoming-call UI — when it is fresh (< 90 s),
//   the call it names is still up with that very call id, the reader is not already in it, and the
//   reader did not write it.
// * The MOVE is a post in the OLD conversation when a 1-to-1 (or a group gaining an outsider) moves
//   to a group: `props.satori_call_moved = {from_channel_id, to_channel_id, call_id}`. A reader who
//   is IN the from-call when it arrives (fresh, someone else's) follows without being asked.
// * Both render in the timeline as a call row: «Unirse» while that call is active, «La llamada
//   terminó» after.
//
// No DOM, no clock of its own: `now` comes in.
import { t, tf } from '../i18n/i18n';
import type { ActiveCall } from './activeCalls';

/** An invitation or a move older than this is history, never a ring or an automatic switch. */
export const CALL_INVITE_FRESH_MS = 90_000;

export const INVITE_PROP = 'satori_call_invite';
export const MOVED_PROP = 'satori_call_moved';

export interface CallInvite {
  channelId: string;
  callId: string;
  /** The call post (the permalink's target); may be '' from a client that did not know it. */
  postId: string;
}

export interface CallMoved {
  fromChannelId: string;
  toChannelId: string;
  callId: string;
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');

function propsObject(props: string | Record<string, unknown> | null | undefined): Record<string, unknown> | null {
  if (!props) return null;
  if (typeof props !== 'string') return props;
  // Cheap guard: almost every post has props that are not ours, and this runs per row drawn.
  if (!props.includes('satori_call_')) return null;
  try {
    const value: unknown = JSON.parse(props);
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function nested(props: string | Record<string, unknown> | null | undefined, key: string): Record<string, unknown> | null {
  const value = propsObject(props)?.[key];
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function inviteFrom(props: string | Record<string, unknown> | null | undefined): CallInvite | null {
  const raw = nested(props, INVITE_PROP);
  if (!raw) return null;
  const channelId = str(raw.channel_id);
  const callId = str(raw.call_id);
  if (!channelId || !callId) return null;
  return { channelId, callId, postId: str(raw.post_id) };
}

function movedFrom(props: string | Record<string, unknown> | null | undefined): CallMoved | null {
  const raw = nested(props, MOVED_PROP);
  if (!raw) return null;
  const fromChannelId = str(raw.from_channel_id);
  const toChannelId = str(raw.to_channel_id);
  const callId = str(raw.call_id);
  if (!fromChannelId || !toChannelId || !callId) return null;
  return { fromChannelId, toChannelId, callId };
}

function inviteProps(invite: CallInvite): Record<string, unknown> {
  return { [INVITE_PROP]: { channel_id: invite.channelId, call_id: invite.callId, post_id: invite.postId } };
}

function movedProps(moved: CallMoved): Record<string, unknown> {
  return { [MOVED_PROP]: { from_channel_id: moved.fromChannelId, to_channel_id: moved.toChannelId, call_id: moved.callId } };
}

// The wire sentences are SPANISH on purpose (§30.3): they are what Mattermost's webapp, a push and
// any old client show, and this workspace speaks Spanish. Our rows draw their own, translated text.
function inviteMessage(title: string, permalink: string): string {
  return `📞 Te invito a la llamada en ${title}: ${permalink}`;
}

function movedMessage(names: readonly string[], permalink: string): string {
  return `📞 La llamada continuó en el grupo con ${names.join(', ')}: ${permalink}`;
}

const INVITE_RE = /^📞 Te invito a la llamada en (.+): (\S+)\s*$/su;
const MOVED_RE = /^📞 La llamada continuó en el grupo con (.+): (\S+)\s*$/su;

function titleFromInviteMessage(message: string): string {
  return INVITE_RE.exec(message)?.[1]?.trim() ?? '';
}

function namesFromMovedMessage(message: string): string {
  return MOVED_RE.exec(message)?.[1]?.trim() ?? '';
}

function fresh(postCreateAt: number, now: number): boolean {
  return now - postCreateAt < CALL_INVITE_FRESH_MS;
}

/**
 * An invite post arriving over the socket rings when ALL hold: someone else wrote it, it is < 90 s
 * old, `activeCallInChannel` (the active call in `invite.channelId`, or null) is still the call it
 * names, and the reader is not already in that call. Being in ANOTHER call does not silence it —
 * the ring is how the reader learns of it; answering leaves the other call.
 */
function shouldRing(
  invite: CallInvite,
  postCreateAt: number,
  now: number,
  activeCallInChannel: { id: string } | null,
  myCurrentCallChannel: string | null,
  authorId: string,
  me: string,
): boolean {
  if (!authorId || authorId === me) return false;
  if (!fresh(postCreateAt, now)) return false;
  if (!activeCallInChannel || activeCallInChannel.id !== invite.callId) return false;
  if (myCurrentCallChannel === invite.channelId) return false;
  return true;
}

/**
 * A move marker sends the reader along (leave the from-call, join `toChannelId`, no ring) only when
 * someone else wrote it, it is < 90 s old and the reader is IN the from-call right now. Anyone else
 * just sees the row and its «Unirse».
 */
function shouldFollowMove(moved: CallMoved, postCreateAt: number, now: number, myCurrentCallChannel: string | null, authorId: string, me: string): boolean {
  if (!authorId || authorId === me) return false;
  if (!fresh(postCreateAt, now)) return false;
  if (moved.toChannelId === moved.fromChannelId) return false;
  return myCurrentCallChannel === moved.fromChannelId;
}

export type CallInviteRowStatus = 'joinable' | 'inCall' | 'ended';

export interface CallInviteRow {
  kind: 'invite' | 'moved';
  text: string;
  status: CallInviteRowStatus;
  /** Where «Unirse» joins: the invite's call channel, or the group a call moved to. */
  channelId: string;
  /** The conversation the call is in (invite), or the group's names (moved) — the call's name on join. */
  title: string;
}

/** What the timeline row says; null for every post that is neither marker. */
function row(args: {
  propsJson: string | null | undefined;
  message: string;
  authorId: string;
  authorName: string;
  me: string;
  calls: readonly ActiveCall[];
  myCallChannelId: string | null;
}): CallInviteRow | null {
  const status = (channelId: string, callId: string): CallInviteRowStatus => {
    const live = args.calls.some((c) => c.channelId === channelId && c.id === callId);
    if (!live) return 'ended';
    return args.myCallChannelId === channelId ? 'inCall' : 'joinable';
  };
  const invite = inviteFrom(args.propsJson);
  if (invite) {
    const title = titleFromInviteMessage(args.message);
    const own = args.authorId === args.me;
    let text: string;
    if (own) text = title ? tf('web.call.invite.rowSent', title) : t('web.call.invite.rowSentNoTitle');
    else text = title ? tf('web.call.invite.row', args.authorName || t('channelList.unknownUser'), title) : tf('web.call.invite.rowNoTitle', args.authorName || t('channelList.unknownUser'));
    return { kind: 'invite', text, status: status(invite.channelId, invite.callId), channelId: invite.channelId, title };
  }
  const moved = movedFrom(args.propsJson);
  if (moved) {
    const names = namesFromMovedMessage(args.message);
    return {
      kind: 'moved',
      text: names ? tf('web.call.invite.movedRow', names) : t('web.call.invite.movedRowNoNames'),
      status: status(moved.toChannelId, moved.callId),
      channelId: moved.toChannelId,
      title: names,
    };
  }
  return null;
}

export const CallInviteSignal = {
  inviteFrom,
  movedFrom,
  inviteProps,
  movedProps,
  inviteMessage,
  movedMessage,
  titleFromInviteMessage,
  namesFromMovedMessage,
  shouldRing,
  shouldFollowMove,
  row,
} as const;
